import React, { useEffect, useMemo, useState } from "react";
import {
  Alert,
  Box,
  Button,
  Chip,
  Dialog,
  DialogActions,
  DialogContent,
  DialogTitle,
  Divider,
  Drawer,
  FormControl,
  InputLabel,
  List,
  ListItem,
  ListItemText,
  MenuItem,
  Select,
  Stack,
  TextField,
  Typography,
} from "@mui/material";
import WarningAmberIcon from "@mui/icons-material/WarningAmberOutlined";
import BlockIcon from "@mui/icons-material/BlockOutlined";
import { DataGrid } from "@mui/x-data-grid";
import {
  addClientWarning,
  deleteClientWarning,
  getClients,
  getClientWarnings,
  setClientBanStatus,
} from "../../api/adminService";
import { formatDate } from "./constants";

export default function ClientWarnings() {
  const [rows, setRows] = useState([]);
  const [rowCount, setRowCount] = useState(0);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState("");
  const [search, setSearch] = useState("");
  const [filter, setFilter] = useState("");
  const [paginationModel, setPaginationModel] = useState({ page: 0, pageSize: 25 });
  const [selectedClient, setSelectedClient] = useState(null);
  const [warningDialogOpen, setWarningDialogOpen] = useState(false);
  const [banDialogTarget, setBanDialogTarget] = useState(null);

  function loadClients() {
    setLoading(true);
    getClients({
      search: search || undefined,
      filter: filter || undefined,
      page: paginationModel.page + 1,
      pageSize: paginationModel.pageSize,
    })
      .then((data) => {
        setRows(data.items);
        setRowCount(data.totalCount);
        setError("");
      })
      .catch(() => setError("Could not load clients. Is the API running?"))
      .finally(() => setLoading(false));
  }

  useEffect(() => {
    loadClients();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [filter, paginationModel]);

  function handleSearchSubmit(e) {
    e.preventDefault();
    setPaginationModel((p) => ({ ...p, page: 0 }));
    loadClients();
  }

  function patchClientRow(userId, patch) {
    setRows((prev) => prev.map((r) => (r.userId === userId ? { ...r, ...patch } : r)));
    setSelectedClient((prev) => (prev?.userId === userId ? { ...prev, ...patch } : prev));
  }

  async function handleUnban(row) {
    const updated = await setClientBanStatus(row.userId, false);
    patchClientRow(row.userId, {
      isBanned: updated.isBanned,
      banReason: updated.banReason,
      bannedAt: updated.bannedAt,
      bannedUntil: updated.bannedUntil,
    });
  }

  const columns = useMemo(
    () => [
      { field: "fullName", headerName: "Name", flex: 1, minWidth: 160 },
      { field: "email", headerName: "Email", flex: 1.2, minWidth: 200 },
      { field: "phone", headerName: "Phone", width: 140, valueGetter: (v) => v || "-" },
      { field: "orderCount", headerName: "Orders", width: 90 },
      {
        field: "warningCount",
        headerName: "Warnings",
        width: 110,
        renderCell: (params) =>
          params.value > 0 ? (
            <Chip size="small" icon={<WarningAmberIcon />} label={params.value} color="warning" />
          ) : (
            <Typography variant="body2" color="text.secondary">
              -
            </Typography>
          ),
      },
      {
        field: "isBanned",
        headerName: "Status",
        width: 170,
        renderCell: (params) =>
          params.value ? (
            <Chip
              size="small"
              icon={<BlockIcon />}
              label={params.row.bannedUntil ? `Until ${formatDate(params.row.bannedUntil)}` : "Blocked"}
              color="error"
            />
          ) : (
            <Chip size="small" label="Active" color="success" />
          ),
      },
      {
        field: "actions",
        headerName: "Actions",
        width: 220,
        sortable: false,
        renderCell: (params) => (
          <Stack direction="row" spacing={1} onClick={(e) => e.stopPropagation()}>
            <Button
              size="small"
              variant="outlined"
              onClick={() => {
                setSelectedClient(params.row);
                setWarningDialogOpen(true);
              }}
            >
              Warn
            </Button>
            {params.row.isBanned ? (
              <Button size="small" color="success" variant="outlined" onClick={() => handleUnban(params.row)}>
                Unblock
              </Button>
            ) : (
              <Button size="small" color="error" variant="outlined" onClick={() => setBanDialogTarget(params.row)}>
                Block
              </Button>
            )}
          </Stack>
        ),
      },
    ],
    // eslint-disable-next-line react-hooks/exhaustive-deps
    []
  );

  return (
    <Box>
      <Stack direction="row" justifyContent="space-between" alignItems="center" sx={{ mb: 3 }}>
        <Typography variant="h4">Warnings &amp; Blocked Clients</Typography>
      </Stack>

      {error && (
        <Alert severity="error" sx={{ mb: 2 }}>
          {error}
        </Alert>
      )}

      <Stack
        component="form"
        onSubmit={handleSearchSubmit}
        direction={{ xs: "column", sm: "row" }}
        spacing={2}
        sx={{ mb: 2 }}
      >
        <TextField
          label="Search name, email or phone"
          value={search}
          onChange={(e) => setSearch(e.target.value)}
          size="small"
          sx={{ minWidth: 260 }}
        />
        <FormControl size="small" sx={{ minWidth: 180 }}>
          <InputLabel>Filter</InputLabel>
          <Select label="Filter" value={filter} onChange={(e) => setFilter(e.target.value)}>
            <MenuItem value="">All clients</MenuItem>
            <MenuItem value="warned">Has warnings</MenuItem>
            <MenuItem value="banned">Blocked</MenuItem>
          </Select>
        </FormControl>
      </Stack>

      <Box sx={{ bgcolor: "background.paper", borderRadius: 2 }}>
        <DataGrid
          autoHeight
          rows={rows}
          getRowId={(row) => row.userId}
          columns={columns}
          rowCount={rowCount}
          loading={loading}
          paginationMode="server"
          paginationModel={paginationModel}
          onPaginationModelChange={setPaginationModel}
          pageSizeOptions={[10, 25, 50, 100]}
          disableRowSelectionOnClick
          onRowClick={(params) => setSelectedClient(params.row)}
          sx={{ border: "none" }}
        />
      </Box>

      <Drawer anchor="right" open={Boolean(selectedClient) && !warningDialogOpen} onClose={() => setSelectedClient(null)}>
        {selectedClient && (
          <ClientDetailPanel
            client={selectedClient}
            onClose={() => setSelectedClient(null)}
            onWarn={() => setWarningDialogOpen(true)}
            onBan={() => setBanDialogTarget(selectedClient)}
            onUnban={() => handleUnban(selectedClient)}
            onWarningDeleted={(warningId) => {
              patchClientRow(selectedClient.userId, {
                warningCount: Math.max(0, (selectedClient.warningCount || 1) - 1),
              });
              return warningId;
            }}
          />
        )}
      </Drawer>

      {warningDialogOpen && selectedClient && (
        <AddWarningDialog
          client={selectedClient}
          onClose={() => setWarningDialogOpen(false)}
          onCreated={() => {
            patchClientRow(selectedClient.userId, { warningCount: (selectedClient.warningCount || 0) + 1 });
            setWarningDialogOpen(false);
          }}
        />
      )}

      {banDialogTarget && (
        <BanClientDialog
          client={banDialogTarget}
          onClose={() => setBanDialogTarget(null)}
          onBanned={(updated) => {
            patchClientRow(banDialogTarget.userId, {
              isBanned: updated.isBanned,
              banReason: updated.banReason,
              bannedAt: updated.bannedAt,
              bannedUntil: updated.bannedUntil,
            });
            setBanDialogTarget(null);
          }}
        />
      )}
    </Box>
  );
}

function ClientDetailPanel({ client, onClose, onWarn, onBan, onUnban }) {
  const [warnings, setWarnings] = useState([]);
  const [loading, setLoading] = useState(true);

  function loadWarnings() {
    setLoading(true);
    getClientWarnings(client.userId)
      .then(setWarnings)
      .finally(() => setLoading(false));
  }

  useEffect(() => {
    loadWarnings();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [client.userId]);

  async function handleDelete(warningId) {
    await deleteClientWarning(warningId);
    setWarnings((prev) => prev.filter((w) => w.warningId !== warningId));
  }

  return (
    <Box sx={{ width: 380, p: 3 }}>
      <Typography variant="h6">{client.fullName}</Typography>
      <Typography variant="body2" color="text.secondary" sx={{ mb: 2 }}>
        {client.email}
      </Typography>

      <Stack spacing={1.5} sx={{ mb: 2 }}>
        <DetailRow label="Phone" value={client.phone || "-"} />
        <DetailRow label="Orders" value={client.orderCount ?? 0} />
        <DetailRow
          label="Status"
          value={
            client.isBanned ? (
              <Chip size="small" icon={<BlockIcon />} label="Blocked" color="error" />
            ) : (
              <Chip size="small" label="Active" color="success" />
            )
          }
        />
        {client.isBanned && client.banReason && <DetailRow label="Block Reason" value={client.banReason} />}
        {client.isBanned && client.bannedAt && <DetailRow label="Blocked On" value={formatDate(client.bannedAt)} />}
        {client.isBanned && (
          <DetailRow
            label="Block Ends"
            value={client.bannedUntil ? formatDate(client.bannedUntil) : "Never (permanent)"}
          />
        )}
      </Stack>

      <Stack direction="row" spacing={1.5} sx={{ mb: 3 }}>
        <Button variant="contained" startIcon={<WarningAmberIcon />} onClick={onWarn} fullWidth>
          Give Warning
        </Button>
        {client.isBanned ? (
          <Button variant="outlined" color="success" onClick={onUnban} fullWidth>
            Unblock
          </Button>
        ) : (
          <Button variant="outlined" color="error" startIcon={<BlockIcon />} onClick={onBan} fullWidth>
            Block
          </Button>
        )}
      </Stack>

      <Divider sx={{ mb: 2 }} />

      <Typography variant="subtitle2" sx={{ mb: 1 }}>
        Warning History
      </Typography>
      {!loading && warnings.length === 0 && (
        <Typography variant="body2" color="text.secondary">
          No warnings on file.
        </Typography>
      )}
      <List dense disablePadding>
        {warnings.map((w) => (
          <ListItem
            key={w.warningId}
            disableGutters
            secondaryAction={
              <Button size="small" color="error" onClick={() => handleDelete(w.warningId)}>
                Remove
              </Button>
            }
            sx={{ alignItems: "flex-start", py: 1 }}
          >
            <ListItemText
              primary={w.reason}
              secondary={
                <>
                  {w.notes && (
                    <Typography component="span" variant="body2" display="block">
                      {w.notes}
                    </Typography>
                  )}
                  <Typography component="span" variant="caption" color="text.secondary">
                    {formatDate(w.createdAt)} · by {w.issuedByName}
                  </Typography>
                </>
              }
            />
          </ListItem>
        ))}
      </List>
    </Box>
  );
}

function AddWarningDialog({ client, onClose, onCreated }) {
  const [reason, setReason] = useState("");
  const [notes, setNotes] = useState("");
  const [error, setError] = useState("");
  const [saving, setSaving] = useState(false);

  async function handleSubmit() {
    if (!reason.trim()) return;
    setSaving(true);
    setError("");
    try {
      await addClientWarning(client.userId, { reason: reason.trim(), notes: notes.trim() || undefined });
      onCreated();
    } catch (err) {
      setError(err.response?.data || "Could not add this warning.");
      setSaving(false);
    }
  }

  return (
    <Dialog open onClose={onClose} fullWidth maxWidth="xs">
      <DialogTitle>Give Warning to {client.fullName}</DialogTitle>
      <DialogContent>
        <Stack spacing={2} sx={{ mt: 1 }}>
          {error && (
            <Alert severity="error">{typeof error === "string" ? error : "Something went wrong."}</Alert>
          )}
          <TextField
            label="Reason"
            value={reason}
            onChange={(e) => setReason(e.target.value)}
            placeholder="e.g. Did not answer phone for delivery"
            fullWidth
            autoFocus
          />
          <TextField
            label="Additional notes (optional)"
            value={notes}
            onChange={(e) => setNotes(e.target.value)}
            multiline
            minRows={2}
            fullWidth
          />
        </Stack>
      </DialogContent>
      <DialogActions>
        <Button onClick={onClose}>Cancel</Button>
        <Button variant="contained" disabled={!reason.trim() || saving} onClick={handleSubmit}>
          Save Warning
        </Button>
      </DialogActions>
    </Dialog>
  );
}

const BAN_DURATION_OPTIONS = [
  { value: "1", label: "1 Day" },
  { value: "3", label: "3 Days" },
  { value: "7", label: "1 Week" },
  { value: "30", label: "1 Month" },
  { value: "", label: "Forever" },
];

function BanClientDialog({ client, onClose, onBanned }) {
  const [reason, setReason] = useState("");
  const [durationDays, setDurationDays] = useState("7");
  const [error, setError] = useState("");
  const [saving, setSaving] = useState(false);

  async function handleSubmit() {
    setSaving(true);
    setError("");
    try {
      const updated = await setClientBanStatus(
        client.userId,
        true,
        reason.trim() || undefined,
        durationDays ? Number(durationDays) : undefined
      );
      onBanned(updated);
    } catch (err) {
      setError(err.response?.data || "Could not block this client.");
      setSaving(false);
    }
  }

  return (
    <Dialog open onClose={onClose} fullWidth maxWidth="xs">
      <DialogTitle>Block {client.fullName}?</DialogTitle>
      <DialogContent>
        <Stack spacing={2} sx={{ mt: 1 }}>
          <Typography variant="body2" color="text.secondary">
            Blocked clients cannot log in to the app until unblocked.
          </Typography>
          {error && (
            <Alert severity="error">{typeof error === "string" ? error : "Something went wrong."}</Alert>
          )}
          <FormControl size="small" fullWidth>
            <InputLabel>Block Duration</InputLabel>
            <Select
              label="Block Duration"
              value={durationDays}
              onChange={(e) => setDurationDays(e.target.value)}
            >
              {BAN_DURATION_OPTIONS.map((opt) => (
                <MenuItem key={opt.label} value={opt.value}>
                  {opt.label}
                </MenuItem>
              ))}
            </Select>
          </FormControl>
          <TextField
            label="Reason (optional)"
            value={reason}
            onChange={(e) => setReason(e.target.value)}
            placeholder="e.g. Repeated failure to answer delivery calls"
            multiline
            minRows={2}
            fullWidth
          />
        </Stack>
      </DialogContent>
      <DialogActions>
        <Button onClick={onClose}>Cancel</Button>
        <Button variant="contained" color="error" disabled={saving} onClick={handleSubmit}>
          Block Client
        </Button>
      </DialogActions>
    </Dialog>
  );
}

function DetailRow({ label, value }) {
  return (
    <Stack direction="row" justifyContent="space-between" alignItems="center">
      <Typography variant="body2" color="text.secondary">
        {label}
      </Typography>
      <Typography variant="body2" fontWeight={600}>
        {value}
      </Typography>
    </Stack>
  );
}
